import { CheckCircle, AlertTriangle, TrendingUp, Anchor } from 'lucide-react';

/**
 * Cards Section - Card layout and shadow comparison
 *
 * Tests:
 * - Basic cards
 * - Status cards with gradients
 * - Interactive cards
 * - Card shadows (subtle vs pronounced)
 */
export default function CardsSection({ theme }) {
  const isDark = theme === 'dark';

  return (
    <div className="space-y-8">
      <div>
        <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Card Comparison
        </h2>
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Current subtle shadows vs iOS-style pronounced shadows and borders
        </p>
      </div>

      {/* Basic Cards */}
      <div className="space-y-4">
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Basic Cards
        </h3>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Current */}
          <div>
            <div className={`text-xs font-semibold uppercase tracking-wider mb-4 ${
              isDark ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Current Design
            </div>
            <div className={`p-6 rounded-lg border ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } shadow-sm`}>
              <h4 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Equipment Status
              </h4>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                All systems operational. Last check: 2 hours ago.
              </p>
              <div className="mt-4 flex gap-2">
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
                  Active
                </span>
                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                  WBC3
                </span>
              </div>
            </div>
          </div>

          {/* iOS-Style */}
          <div>
            <div className={`text-xs font-bold uppercase tracking-wider mb-4 ${
              isDark ? 'text-blue-400' : 'text-blue-600'
            }`}>
              iOS-Style (Enhanced)
            </div>
            <div className={`p-6 rounded-xl border-2 ${
              isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
            } shadow-lg hover:shadow-xl transition-shadow`}>
              <h4 className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Equipment Status
              </h4>
              <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                All systems operational. Last check: 2 hours ago.
              </p>
              <div className="mt-4 flex gap-2">
                <span className="px-2 py-1 bg-green-500 text-white text-xs font-bold rounded-md shadow-sm">
                  Active
                </span>
                <span className="px-2 py-1 bg-blue-500 text-white text-xs font-bold rounded-md shadow-sm">
                  WBC3
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Cards */}
      <div className="space-y-4">
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Status Cards with Icons
        </h3>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Current */}
          <div className="space-y-3">
            <div className={`p-4 rounded-lg border ${
              isDark ? 'bg-gray-800 border-green-700' : 'bg-white border-green-200'
            } shadow-sm`}>
              <div className="flex items-start gap-3">
                <CheckCircle className="text-green-600" size={24} />
                <div className="flex-1">
                  <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Safety Check Complete
                  </h4>
                  <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    All safety equipment verified
                  </p>
                </div>
              </div>
            </div>

            <div className={`p-4 rounded-lg border ${
              isDark ? 'bg-gray-800 border-amber-700' : 'bg-white border-amber-200'
            } shadow-sm`}>
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-amber-600" size={24} />
                <div className="flex-1">
                  <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Maintenance Due
                  </h4>
                  <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    3 items require attention
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* iOS-Style */}
          <div className="space-y-3">
            <div className={`p-4 rounded-xl border-2 ${
              isDark ? 'bg-gray-800 border-green-500' : 'bg-white border-green-300'
            } shadow-lg hover:shadow-xl transition-all`}>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <CheckCircle className="text-green-600 dark:text-green-400" size={24} strokeWidth={2.5} />
                </div>
                <div className="flex-1">
                  <h4 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Safety Check Complete
                  </h4>
                  <p className={`text-sm mt-1 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    All safety equipment verified
                  </p>
                </div>
              </div>
            </div>

            <div className={`p-4 rounded-xl border-2 ${
              isDark ? 'bg-gray-800 border-amber-500' : 'bg-white border-amber-300'
            } shadow-lg hover:shadow-xl transition-all`}>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                  <AlertTriangle className="text-amber-600 dark:text-amber-400" size={24} strokeWidth={2.5} />
                </div>
                <div className="flex-1">
                  <h4 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Maintenance Due
                  </h4>
                  <p className={`text-sm mt-1 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    3 items require attention
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="space-y-4">
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Statistic Cards
        </h3>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Current */}
          <div className={`p-6 rounded-lg ${
            isDark ? 'bg-gray-800' : 'bg-white'
          } shadow-sm border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="text-blue-600" size={32} />
              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
                +12%
              </span>
            </div>
            <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              92%
            </div>
            <div className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Compliance Score
            </div>
          </div>

          {/* iOS-Style */}
          <div className={`p-6 rounded-xl ${
            isDark ? 'bg-gradient-to-br from-blue-900 to-blue-800' : 'bg-gradient-to-br from-blue-500 to-blue-600'
          } shadow-xl text-white`}>
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                <TrendingUp size={32} strokeWidth={2.5} />
              </div>
              <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-lg shadow-md">
                +12%
              </span>
            </div>
            <div className="text-4xl font-black tracking-tight">
              92%
            </div>
            <div className="text-sm mt-1 font-semibold text-blue-100">
              Compliance Score
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
